import actorApi from '../../api/actorApi';
import tagApi from '../../api/tagApi';
import userApi from '../../api/userApi';

const PAGE_SIZE = 100;
const MAX_PAGES = 1000;

// The actor service returns an exact page count only on the first page.
export const loadAllActors = async () => {
  const filters = { sort_by: 'id', sort_direction: 'asc' };
  const first = await actorApi.getActors(1, PAGE_SIZE, filters);
  const totalPages = first.total_pages;
  if (!Number.isSafeInteger(totalPages) || totalPages < 1 || totalPages > MAX_PAGES) {
    throw new Error('Unable to load the complete actor list');
  }

  const actors = new Map();
  const addPage = (data, page) => {
    if (!Array.isArray(data.actors) || (data.current_page ?? data.page ?? page) !== page) {
      throw new Error('Unable to load the complete actor list');
    }
    data.actors.forEach(actor => {
      if (actor?.id) actors.set(String(actor.id), actor);
    });
  };
  addPage(first, 1);
  for (let page = 2; page <= totalPages; page += 1) {
    addPage(await actorApi.getActors(page, PAGE_SIZE, filters), page);
  }
  return [...actors.values()];
};

// An account beyond the first user page must still prevent another login grant.
export const loadAllUsers = async () => {
  const users = [];
  const seen = new Set();
  for (let page = 0; page < MAX_PAGES; page += 1) {
    const batch = await userApi.getUsers({ skip: page * PAGE_SIZE, limit: PAGE_SIZE });
    if (!Array.isArray(batch) || batch.some(user => !user?.id || seen.has(String(user.id)))) {
      throw new Error('Unable to load the complete user list');
    }
    batch.forEach(user => seen.add(String(user.id)));
    users.push(...batch);
    if (batch.length < PAGE_SIZE) return users;
  }
  throw new Error('Unable to load the complete user list');
};

export const loadActorTags = async actors => {
  if (actors.length === 0) return {};
  const entityTypes = await tagApi.getEntityTypes();
  const actorType = entityTypes.find(type => type.name?.trim().toLowerCase() === 'actor');
  if (!actorType) throw new Error('Actor tags are unavailable');

  const tags = {};
  for (let start = 0; start < actors.length; start += PAGE_SIZE) {
    const ids = actors.slice(start, start + PAGE_SIZE).map(actor => String(actor.id));
    const response = await tagApi.getBatchEntityTags(actorType.id, ids);
    if (ids.some(id => !Array.isArray(response?.tags_by_entity?.[id]))) {
      throw new Error('Unable to load actor tags');
    }
    Object.assign(tags, response.tags_by_entity);
  }
  return tags;
};

export const hasNonCustomerTag = tags => Array.isArray(tags) && tags.some(tag => {
  const name = typeof tag?.name === 'string' ? tag.name.trim().toLowerCase() : '';
  return name !== '' && name !== 'customer';
});

/**
 * Everybody an account can be granted to. Staff-tagged contacts come first;
 * a contact with no tag yet, or only Customer, is still listed and marked,
 * because a contact created a minute ago must be findable here.
 */
export const buildPeopleList = (actors, users, actorTags, options = {}) => {
  const actorById = new Map(actors.map(actor => [String(actor.id), actor]));
  const linkedActorIds = new Set(users.filter(user => user.actor_id).map(user => String(user.actor_id)));

  // Preserve every existing account, including customer-only and unlinked users.
  const withLogin = users.map(user => ({
    actor: actorById.get(String(user.actor_id)) || {
      id: user.actor_id || `user-${user.id}`,
      name: user.email,
      email: user.email,
      type: 'user',
    },
    user,
    hasLogin: true,
  }));
  const includeEveryContact = options.includeUntagged === true;
  const withoutLogin = actors
    .filter(actor => !linkedActorIds.has(String(actor.id)))
    .map(actor => ({ actor, user: null, hasLogin: false, staffTagged: hasNonCustomerTag(actorTags[actor.id]) }))
    .filter(person => includeEveryContact || person.staffTagged);
  return [...withLogin, ...withoutLogin];
};
